import { createContext, useReducer, useEffect, useMemo, useContext, useCallback, type ReactNode } from 'react';
import CssBaseline from '@mui/material/CssBaseline';
import { ThemeProvider as MuiThemeProvider, useColorScheme } from '@mui/material/styles';
import { createAppTheme, getBoxShadow, getChromeThemeColor } from '../config/ui/theme';
import { useAuth } from './AuthContext';
import { updateUserPreferences } from '../config/data/firebase';
import { collections } from '../config/data/collections';

interface ThemeState {
	darkMode: boolean;
	primaryColor: string;
}

type ThemeAction =
	| { type: 'LIGHT' }
	| { type: 'DARK' }
	| { type: 'TOGGLE' }
	| { type: 'SET_COLOR'; payload: string }
	| { type: 'LOAD_PREFERENCES'; payload: { darkMode?: boolean; primaryColor?: string } };

export interface ThemeContextValue {
	darkMode: boolean;
	primaryColor: string;
	dispatch: (action: ThemeAction) => void;
	boxShadow: string;
}

const getInitialState = (): ThemeState => {
	const savedPrefs = localStorage.getItem('pf_theme_prefs');
	if (savedPrefs) {
		return JSON.parse(savedPrefs) as ThemeState;
	}
	return {
		darkMode: globalThis.matchMedia?.('(prefers-color-scheme: dark)')?.matches ?? false,
		primaryColor: 'green',
	};
};

const ThemeReducer = (state: ThemeState, action: ThemeAction): ThemeState => {
	const newState = { ...state };
	let hasChanges = false;

	switch (action.type) {
		case 'LIGHT':
			newState.darkMode = false;
			hasChanges = true;
			break;
		case 'DARK':
			newState.darkMode = true;
			hasChanges = true;
			break;
		case 'TOGGLE':
			newState.darkMode = !state.darkMode;
			hasChanges = true;
			break;
		case 'SET_COLOR':
			newState.primaryColor = action.payload;
			hasChanges = true;
			break;
		case 'LOAD_PREFERENCES':
			newState.darkMode = action.payload.darkMode ?? state.darkMode;
			newState.primaryColor = action.payload.primaryColor ?? state.primaryColor;
			hasChanges = true;
			break;
		default:
			return state;
	}

	if (hasChanges) {
		localStorage.setItem('pf_theme_prefs', JSON.stringify(newState));
	}

	return newState;
};

export const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

/** Keep MUI color-scheme class in sync with app preference without recreating the theme. */
const ColorSchemeSync = ({ darkMode }: { darkMode: boolean }) => {
	const { mode, setMode } = useColorScheme();

	useEffect(() => {
		const next = darkMode ? 'dark' : 'light';
		if (mode !== next) {
			setMode(next);
		}
	}, [darkMode, mode, setMode]);

	return null;
};

export const ThemeProvider = ({ children }: { children: ReactNode }) => {
	const [state, dispatch] = useReducer(ThemeReducer, undefined, getInitialState);
	const { member, applicant, user } = useAuth();

	const currentUserProfile = member || applicant;
	let currentCollection: string | null = null;
	if (member) {
		currentCollection = collections.members;
	} else if (applicant) {
		currentCollection = collections.applicants;
	}

	useEffect(() => {
		if ((currentUserProfile as Record<string, unknown> | null)?.preferences) {
			const prefs = (currentUserProfile as Record<string, unknown>).preferences as Record<string, unknown>;
			dispatch({
				type: 'LOAD_PREFERENCES',
				payload: {
					darkMode: prefs.darkMode as boolean | undefined,
					primaryColor: prefs.primaryColor as string | undefined,
				},
			});
		}
	}, [currentUserProfile]);

	useEffect(() => {
		if (!globalThis.matchMedia) return;
		const mediaQuery = globalThis.matchMedia('(prefers-color-scheme: dark)');

		const hasLocalSave = localStorage.getItem('pf_theme_prefs');

		const handleChange = (e: MediaQueryListEvent) => {
			if (!(currentUserProfile as Record<string, unknown> | null)?.preferences && !hasLocalSave) {
				dispatch({ type: e.matches ? 'DARK' : 'LIGHT' });
			}
		};

		mediaQuery.addEventListener('change', handleChange);
		return () => mediaQuery.removeEventListener('change', handleChange);
	}, [currentUserProfile]);

	// Keep Chromium/PWA chrome on navbar/page surfaces (not brand accent).
	useEffect(() => {
		const chromeColor = getChromeThemeColor(state.darkMode, state.primaryColor);
		document.documentElement.style.backgroundColor = chromeColor;
		document.documentElement.style.colorScheme = state.darkMode ? 'dark' : 'light';
		for (const meta of document.querySelectorAll('meta[name="theme-color"]')) {
			meta.setAttribute('content', chromeColor);
		}
		const tileMeta = document.querySelector('meta[name="msapplication-TileColor"]');
		if (tileMeta) tileMeta.setAttribute('content', chromeColor);
	}, [state.darkMode, state.primaryColor]);

	const dispatchWithSave = useCallback(
		(action: ThemeAction) => {
			dispatch(action);

			const updates: Record<string, unknown> = {};

			if (action.type === 'TOGGLE') updates.darkMode = !state.darkMode;
			if (action.type === 'LIGHT') updates.darkMode = false;
			if (action.type === 'DARK') updates.darkMode = true;
			if (action.type === 'SET_COLOR') updates.primaryColor = action.payload;

			if (Object.keys(updates).length > 0 && user && currentCollection) {
				updateUserPreferences(user.uid, currentCollection, updates);
			}
		},
		[currentCollection, state.darkMode, user]
	);

	const theme = useMemo(() => createAppTheme(state.primaryColor), [state.primaryColor]);
	const defaultMode = state.darkMode ? 'dark' : 'light';

	const value = useMemo<ThemeContextValue>(
		() => ({
			darkMode: state.darkMode,
			primaryColor: state.primaryColor,
			dispatch: dispatchWithSave,
			boxShadow: getBoxShadow(state.darkMode),
		}),
		[state.darkMode, state.primaryColor, dispatchWithSave]
	);

	return (
		<ThemeContext.Provider value={value}>
			<MuiThemeProvider
				theme={theme}
				defaultMode={defaultMode}
				storageManager={null}
				disableTransitionOnChange
				noSsr
			>
				<CssBaseline enableColorScheme />
				<ColorSchemeSync darkMode={state.darkMode} />
				{children}
			</MuiThemeProvider>
		</ThemeContext.Provider>
	);
};

export const useTheme = (): ThemeContextValue => {
	const context = useContext(ThemeContext);
	if (context === undefined) {
		throw new Error('useTheme must be used within a ThemeProvider');
	}
	return context;
};
